import React, { useState } from "react";
import { Formik, Form } from "formik";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";

import { useAuthentication } from "store/backend/apiHooks";
import { ErrorMessage, PrimaryButton } from "widgets";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import Loader from "shared/Icons/Loader/Loader";

import styles from "./Login.module.scss";

const APP_TITLE = "Tenant Management Dashboard";

const loginSchema = Yup.object({
  mobile: Yup.string()
    .required("Mobile number is required")
    .matches(/^[0-9]{10}$/, "Mobile number must be exactly 10 digits"),
  password: Yup.string()
    .required("Password is required")
    .min(1, "Password must contain at least 8 character"),
});

const initialValues = {
  mobile: "",
  password: "",
};

const handleLoginFormKeyDown = (event, submitForm) => {
  if (event.key !== "Enter") return;
  if (event.target?.type === "button") return;
  event.preventDefault();
  submitForm();
};

const handleSubmit = (values, login, navigate, setSubmitting, openSnackbar) => {
  login(
    {
      mobile: values.mobile,
      password: values.password,
    },
    {
      onSuccess: () => {
        openSnackbar("Login Successful", "success");
        navigate("/tenants");
      },
      onError: (response) => {
        const errorMessages = response.message;
        openSnackbar(errorMessages || "Error fetching tax invoice", "error");
      },
      onSettled: () => {
        setSubmitting(false);
      },
    }
  );
};

const LoginHeader = () => (
  <header className={styles.loginHeader}>
    <div className={styles.iconBadge} aria-hidden>
      <LockOutlinedIcon />
    </div>
    <h1 className={styles.loginTitle}>Welcome back to {APP_TITLE}</h1>
    <p className={styles.loginSubtitle}>Sign in to your account</p>
  </header>
);

const LoginField = ({
  id,
  label,
  type = "text",
  placeholder,
  value,
  onChange,
  onBlur,
  error,
  errorMessage,
  trailing,
  inputMode,
  autoComplete,
}) => (
  <div className={styles.fieldGroup}>
    <label className={styles.fieldLabel} htmlFor={id}>
      {label}
    </label>
    <div className={`${styles.inputWrapper} ${error ? styles.inputWrapperError : ""}`}>
      <input
        id={id}
        name={id}
        type={type}
        className={styles.fieldInput}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        inputMode={inputMode}
        autoComplete={autoComplete}
      />
      {trailing}
    </div>
    {error && errorMessage ? (
      <ErrorMessage>{errorMessage}</ErrorMessage>
    ) : null}
  </div>
);

const LoginForm = () => {
  const navigate = useNavigate();
  const { openSnackbar } = useSnackbarContext();
  const { mutate: login, isPending } = useAuthentication();
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className={styles.loginCard}>
      <LoginHeader />

      <Formik
        initialValues={initialValues}
        validationSchema={loginSchema}
        validateOnMount
        onSubmit={(values, { setSubmitting }) =>
          handleSubmit(values, login, navigate, setSubmitting, openSnackbar)
        }
      >
        {(formikProps) => {
          const {
            isValid,
            values,
            errors,
            touched,
            isSubmitting,
            setFieldValue,
            handleBlur,
            submitForm,
          } = formikProps;

          return (
            <Form
              className={styles.loginForm}
              noValidate
              onKeyDown={(event) => handleLoginFormKeyDown(event, submitForm)}
            >
              <LoginField
                id="mobile"
                label="Phone Number"
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                placeholder="Enter your Phone number"
                value={values.mobile}
                onBlur={handleBlur}
                onChange={(event) =>
                  setFieldValue(
                    "mobile",
                    event.target.value.replace(/\D/g, "").slice(0, 10)
                  )
                }
                error={touched.mobile && errors.mobile}
                errorMessage={errors.mobile}
              />

              <LoginField
                id="password"
                label="Password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                placeholder="Enter your password"
                value={values.password}
                onBlur={handleBlur}
                onChange={(event) =>
                  setFieldValue("password", event.target.value)
                }
                error={touched.password && errors.password}
                errorMessage={errors.password}
                trailing={
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <VisibilityOffOutlinedIcon fontSize="small" />
                    ) : (
                      <VisibilityOutlinedIcon fontSize="small" />
                    )}
                  </button>
                }
              />

              <PrimaryButton
                type="submit"
                className={styles.signInButton}
                disabled={!isValid || isSubmitting}
                fullWidth
              >
                {isPending ? <Loader /> : "Sign In"}
              </PrimaryButton>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

const Login = () => (
  <div className={styles.rootContainer}>
    <LoginForm />
  </div>
);

export default Login;
